/**
 * Clover Menu Service for Festival Map Integration
 * Handles fetching and displaying vendor menus from Clover POS
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Cache configuration for vendor menus
 */
const CACHE_KEY_PREFIX = 'clover_vendor_menu_cache_';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Get cached menu data for a specific vendor
 */
const getCachedMenu = (vendorId) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    if (now - timestamp < CACHE_DURATION) {
      console.log(`📱 Using cached Clover menu data for vendor ${vendorId}`);
      return data;
    }
    
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error('Error reading Clover menu cache:', error);
    return null;
  }
};

/**
 * Cache menu data for a specific vendor
 */
const setCachedMenu = (vendorId, data) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`💾 Cached Clover menu data for vendor ${vendorId}`);
  } catch (error) {
    console.error('Error caching Clover menu data:', error);
  }
};

/**
 * Fetch menu data for a specific vendor
 */
export const fetchCloverVendorMenu = async (vendorId) => {
  try {
    console.log(`🔄 Fetching Clover menu for vendor: ${vendorId}`);
    console.log(`🔗 API_BASE_URL: ${API_BASE_URL}`);
    console.log(`🔗 Full URL: ${API_BASE_URL}/api/clover/menu/${vendorId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/clover/menu/${vendorId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch Clover menu');
    }

    console.log(`✅ Clover menu fetched successfully for vendor ${vendorId}:`, result.data);
    return result.data;

  } catch (error) {
    console.error(`❌ Error fetching Clover menu for vendor ${vendorId}:`, error);
    throw new Error(`Failed to fetch Clover menu: ${error.message}`);
  }
};

/**
 * Get menu data for a vendor (with caching)
 */
export const getCloverVendorMenu = async (vendorId) => {
  try {
    // Check cache first
    const cachedData = getCachedMenu(vendorId);
    if (cachedData) {
      return cachedData;
    }

    // Fetch fresh data
    const menuData = await fetchCloverVendorMenu(vendorId);
    
    // Cache the data
    setCachedMenu(vendorId, menuData);
    
    return menuData;
  } catch (error) {
    console.error(`❌ Failed to get Clover menu for vendor ${vendorId}:`, error);
    throw error;
  }
};

/**
 * Create order for a vendor
 */
export const createCloverVendorOrder = async (vendorId, orderData) => {
  try {
    console.log(`🛒 Creating Clover order for vendor: ${vendorId}`, orderData);
    
    const response = await fetch(`${API_BASE_URL}/api/clover/order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendorId,
        items: orderData.items,
        customerInfo: orderData.customerInfo
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create Clover order');
    }

    console.log(`✅ Clover order created successfully for vendor ${vendorId}:`, result);
    return result;

  } catch (error) {
    console.error(`❌ Error creating Clover order for vendor ${vendorId}:`, error);
    throw new Error(`Failed to create Clover order: ${error.message}`);
  }
};

/**
 * Get vendor information
 */
export const getCloverVendorInfo = async (vendorId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/clover/vendors`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch Clover vendor info');
    }

    const vendor = result.vendors.find(v => v.id === vendorId);
    return vendor || null;

  } catch (error) {
    console.error(`❌ Error fetching Clover vendor info for ${vendorId}:`, error);
    throw error;
  }
};

/**
 * Get all connected Clover vendors
 */
export const getConnectedCloverVendors = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/clover/vendors`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch connected Clover vendors');
    }

    return result.vendors;

  } catch (error) {
    console.error('❌ Error fetching connected Clover vendors:', error);
    throw error;
  }
};

/**
 * Clear menu cache for a specific vendor
 */
export const clearCloverMenuCache = (vendorId) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${vendorId}`;
    localStorage.removeItem(cacheKey);
    console.log(`🗑️ Cleared Clover menu cache for vendor ${vendorId}`);
  } catch (error) {
    console.error('Error clearing Clover menu cache:', error);
  }
};

/**
 * Get popular items from menu data
 */
export const getCloverPopularItems = (menuData, count = 5) => {
  if (!menuData || !menuData.menu) return [];
  
  const allItems = [];
  Object.values(menuData.menu).forEach(category => {
    if (Array.isArray(category)) {
      allItems.push(...category);
    }
  });
  
  // For now, return first N items (in real implementation, you'd use popularity data)
  return allItems.slice(0, count);
};

/**
 * Search menu items
 */
export const searchCloverMenuItems = (menuData, query) => {
  if (!menuData || !menuData.menu || !query) return [];
  
  const results = [];
  const searchTerm = query.toLowerCase();
  
  Object.values(menuData.menu).forEach(category => {
    if (Array.isArray(category)) {
      category.forEach(item => {
        if (item.name.toLowerCase().includes(searchTerm) || 
            item.description.toLowerCase().includes(searchTerm)) {
          results.push(item);
        }
      });
    }
  });
  
  return results;
}; 